"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ConfidenceBadge } from "@/components/confidence-badge"
import {
  Waves,
  Wind,
  Droplets,
  ParkingCircle,
  Wifi,
  ShowerHead,
  UtensilsCrossed,
  LifeBuoy,
  Users,
  TrendingUp,
  Lock,
  Star,
} from "lucide-react"
import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import Link from "next/link"

interface BadgeWithCount {
  label: string
  count: number
}

interface AggregatedAttributes {
  spot_id: string
  name: string
  total_reviews: number
  aggregated_wave_type: string | null
  aggregated_skill_level: string | null
  aggregated_best_wind_direction: string | null
  aggregated_best_swell_direction: string | null
  most_common_wave_condition: string | null
  most_common_wind_condition: string | null
  most_common_water_condition: string | null
  confirmed_facilities: BadgeWithCount[] | string[] | null // Can be JSON array or string array
  confirmed_safety_features: BadgeWithCount[] | string[] | null
  confirmed_accessibility: BadgeWithCount[] | string[] | null
  weighted_overall_rating: number | null
  typical_crowd_level: number | null
  recent_review_count: number
  confidence_level: "high" | "medium" | "low" | "none"
}

interface AggregatedSpotAttributesProps {
  spotId: string
  user?: any // Added user prop
  refreshTrigger?: number // Trigger refetch when this changes
  onWriteReview?: () => void
  isMobile?: boolean
}

export function AggregatedSpotAttributes({ spotId, user, refreshTrigger, onWriteReview, isMobile }: AggregatedSpotAttributesProps) {
  const [attributes, setAttributes] = useState<AggregatedAttributes | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      setLoading(true) // Show loading state when refreshing
      fetchAggregatedAttributes()
    } else {
      const dummyAttributes: AggregatedAttributes = {
        spot_id: spotId,
        name: "Dummy Spot",
        total_reviews: 47,
        most_common_wave_condition: "Clean & Glassy",
        most_common_wind_condition: "Light Offshore",
        most_common_water_condition: "Clear",
        confirmed_facilities: ["Parking", "Showers", "Restaurant"],
        confirmed_safety_features: ["Lifeguard"],
        confirmed_accessibility: ["Easy Access"],
        weighted_overall_rating: 4.3,
        typical_crowd_level: 3,
        recent_review_count: 12,
        confidence_level: "high",
      }
      setAttributes(dummyAttributes)
      setLoading(false)
    }
  }, [spotId, user, refreshTrigger]) // Added refreshTrigger as dependency

  const fetchAggregatedAttributes = async () => {
    const supabase = createClient()

    try {
      const { data, error } = await supabase
        .from("spot_attributes_aggregated")
        .select("*")
        .eq("spot_id", spotId)
        .single()

      if (error) {
        console.error("[AggregatedSpotAttributes] Error fetching aggregated attributes:", error)
        // If no data found, set attributes to null to show "no data" message
        setAttributes(null)
        setLoading(false)
        return
      }

      setAttributes(data)
    } catch (error) {
      console.error("[AggregatedSpotAttributes] Error:", error)
      setAttributes(null)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
              Spot Breakdown
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-8 pt-0">
          <div className="space-y-4 pt-4">
            <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse" />
            <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse w-3/4" />
            <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse w-1/2" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!attributes || attributes.total_reviews === 0) {
    return (
      <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
              Spot Breakdown
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-10 flex flex-col items-center justify-center text-center space-y-6">
          <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800/50 rounded-full flex items-center justify-center">
            <TrendingUp className="w-10 h-10 text-slate-300 dark:text-slate-600" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">No community data yet</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[240px] leading-relaxed">
              Be the first to review this spot and help the community with real-time conditions!
            </p>
          </div>
          <Button 
            onClick={onWriteReview}
            className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold shadow-xl shadow-blue-600/20 active:scale-95 transition-all flex items-center gap-2"
          >
            <Star className="w-5 h-5 fill-white" />
            Review Spot
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Debug: Log what we have
  if (process.env.NODE_ENV === "development") {
    console.log("[AggregatedSpotAttributes] Rendering with attributes:", {
      total_reviews: attributes.total_reviews,
      has_facilities: !!attributes.confirmed_facilities && attributes.confirmed_facilities.length > 0,
      facilities_count: attributes.confirmed_facilities?.length || 0,
      has_safety: !!attributes.confirmed_safety_features && attributes.confirmed_safety_features.length > 0,
      has_accessibility: !!attributes.confirmed_accessibility && attributes.confirmed_accessibility.length > 0,
      has_wave_condition: !!attributes.most_common_wave_condition,
      has_wind_condition: !!attributes.most_common_wind_condition,
      has_water_condition: !!attributes.most_common_water_condition,
    })
  }

  const getCrowdLevelText = (level: number | null) => {
    if (!level) return "Unknown"
    if (level <= 2) return "Usually uncrowded"
    if (level <= 3) return "Moderate crowds"
    return "Often crowded"
  }

  // Format label: convert snake_case to Title Case
  const formatLabel = (label: string) => {
    return label
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  // Map filter values to their emojis from the review form
  const getFilterEmoji = (value: string): string | null => {
    const emojiMap: Record<string, string> = {
      // Core attributes
      beach_break: "🏖️",
      reef_break: "🪸",
      point_break: "🌊",
      river_mouth: "🏞️",
      beginner: "🟢",
      intermediate: "🟡",
      advanced: "🟠",
      expert: "🔴",
      n: "⬆️",
      ne: "↗️",
      e: "➡️",
      se: "↘️",
      s: "⬇️",
      sw: "↙️",
      w: "⬅️",
      nw: "↖️",
      // Wave conditions
      small_waves: "🌊",
      medium_waves: "🌊",
      big_waves: "🌊",
      safe_sandy: "🏖️",
      rocky_bottom: "🪨",
      shallow_reef: "🪸",
      sharp_reef: "⚠️",
      // Wind conditions
      strong_wind: "💨",
      offshore: "🌬️",
      side_shore: "↔️",
      light_winds: "🍃",
      consistent_wind: "🌪️",
      // Water conditions
      flat_water: "🏞️",
      choppy_water: "🌊",
      warm_water: "🌡️",
      cold_water: "🧊",
      deep_water: "🏊‍♂️",
      shallow_water: "🦶",
      pristine: "✨",
      very_clean: "🌿",
      moderate_clean: "🌱",
      // Safety features
      safe_launch: "🛟",
      lifeguards: "👨‍⚕️",
      no_hazards: "✅",
      safe_kids: "👶",
      good_visibility: "👁️",
      // Facilities
      wifi: "📶",
      surf_schools: "🏫",
      easy_access: "🚗",
      gear_rental: "🏄‍♂️",
      repair_shops: "🔧",
      restaurants: "🍽️",
      cafes: "☕",
      seafood: "🦐",
      vegetarian: "🥗",
      // Accessibility/Season
      year_round: "🗓️",
      summer: "☀️",
      winter: "❄️",
      windy_season: "🌬️",
      // Cost
      budget: "💚",
      moderate: "💛",
      expensive: "🧡",
      // Climate
      tropical: "🌴",
      warm_climate: "☀️",
      mild: "⛅",
      cool: "🌡️",
    }
    return emojiMap[value.toLowerCase()] || null
  }

  const facilityIcons: Record<string, any> = {
    parking: ParkingCircle,
    wifi: Wifi,
    showers: ShowerHead,
    restaurant: UtensilsCrossed,
    lifeguard: LifeBuoy,
  }

  return (
    <div className="relative">
      <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden ${!user ? "blur-md" : ""}`}>
        <CardHeader className="p-8 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
              Spot Breakdown
            </CardTitle>
            {attributes && (
              <ConfidenceBadge level={attributes.confidence_level} reviewCount={attributes.total_reviews} />
            )}
          </div>
          {attributes && (
            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
              Based on {attributes.total_reviews} review{attributes.total_reviews !== 1 ? "s" : ""}
              {attributes.recent_review_count > 0 && ` (${attributes.recent_review_count} recent)`}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-8 pt-0">
          {/* Tag Cloud - All badges together */}
          {(() => {
            // Helper to parse facilities/safety/accessibility (can be JSON array or string array)
            const parseBadgeArray = (arr: any): Array<{ label: string; count: number }> => {
              if (!arr || !Array.isArray(arr)) return []
              return arr.map((item: any) => {
                if (typeof item === 'string') {
                  return { label: item, count: 1 } // Fallback for old format
                }
                return { label: item.label || item, count: item.count || 1 }
              })
            }

            // Collect all badges into one array with icon color and popularity info
            const allBadges: Array<{ 
              label: string
              emoji?: string
              icon?: any
              iconColor: string
              count: number
            }> = []

            // Add core attributes (blue/indigo icons)
            if (attributes.aggregated_wave_type) {
              const emoji = getFilterEmoji(attributes.aggregated_wave_type)
              allBadges.push({ 
                label: formatLabel(attributes.aggregated_wave_type), 
                emoji: emoji || "🌊",
                iconColor: "text-blue-500",
                count: attributes.total_reviews
              })
            }
            if (attributes.aggregated_skill_level) {
              const emoji = getFilterEmoji(attributes.aggregated_skill_level)
              allBadges.push({ 
                label: formatLabel(attributes.aggregated_skill_level), 
                emoji: emoji || "📈",
                iconColor: "text-blue-600",
                count: attributes.total_reviews
              })
            }
            if (attributes.aggregated_best_swell_direction) {
              const emoji = getFilterEmoji(attributes.aggregated_best_swell_direction)
              const directionLabel = {
                N: "North Swell", NE: "Northeast Swell", E: "East Swell", SE: "Southeast Swell",
                S: "South Swell", SW: "Southwest Swell", W: "West Swell", NW: "Northwest Swell"
              }[attributes.aggregated_best_swell_direction] || attributes.aggregated_best_swell_direction
              allBadges.push({ 
                label: directionLabel, 
                emoji: emoji || "🌊",
                iconColor: "text-blue-400",
                count: attributes.total_reviews
              })
            }
            if (attributes.aggregated_best_wind_direction) {
              const emoji = getFilterEmoji(attributes.aggregated_best_wind_direction)
              const directionLabel = {
                N: "North Wind", NE: "Northeast Wind", E: "East Wind", SE: "Southeast Wind",
                S: "South Wind", SW: "Southwest Wind", W: "West Wind", NW: "Northwest Wind"
              }[attributes.aggregated_best_wind_direction] || attributes.aggregated_best_wind_direction
              allBadges.push({ 
                label: directionLabel, 
                emoji: emoji || "🌬️",
                iconColor: "text-cyan-400",
                count: attributes.total_reviews
              })
            }

            // Add conditions (blue/cyan/teal icons) - use total_reviews as count estimate
            if (attributes.most_common_wave_condition) {
              const emoji = getFilterEmoji(attributes.most_common_wave_condition)
              allBadges.push({ 
                label: attributes.most_common_wave_condition, 
                emoji: emoji || "🌊",
                iconColor: "text-blue-500",
                count: attributes.total_reviews // Estimate: all reviews mention this condition
              })
            }
            if (attributes.most_common_wind_condition) {
              const emoji = getFilterEmoji(attributes.most_common_wind_condition)
              allBadges.push({ 
                label: attributes.most_common_wind_condition, 
                emoji: emoji || undefined,
                icon: emoji ? undefined : Wind,
                iconColor: "text-cyan-500",
                count: attributes.total_reviews
              })
            }
            if (attributes.most_common_water_condition) {
              const emoji = getFilterEmoji(attributes.most_common_water_condition)
              allBadges.push({ 
                label: attributes.most_common_water_condition, 
                emoji: emoji || undefined,
                icon: emoji ? undefined : Droplets,
                iconColor: "text-teal-500",
                count: attributes.total_reviews
              })
            }

            // Add crowd level (purple icon)
            if (attributes.typical_crowd_level) {
              allBadges.push({ 
                label: getCrowdLevelText(attributes.typical_crowd_level), 
                icon: Users,
                iconColor: "text-purple-500",
                count: attributes.total_reviews
              })
            }

            // Add facilities (green icons) with actual counts
            const facilities = parseBadgeArray(attributes.confirmed_facilities)
            facilities.forEach((facility) => {
              const emoji = getFilterEmoji(facility.label)
              allBadges.push({ 
                label: facility.label, 
                emoji: emoji || undefined,
                icon: emoji ? undefined : (facilityIcons[facility.label.toLowerCase()] || ParkingCircle),
                iconColor: "text-green-500",
                count: facility.count
              })
            })

            // Add safety features (orange icons) with actual counts
            const safetyFeatures = parseBadgeArray(attributes.confirmed_safety_features)
            safetyFeatures.forEach((feature) => {
              const emoji = getFilterEmoji(feature.label)
              allBadges.push({ 
                label: feature.label, 
                emoji: emoji || undefined,
                icon: emoji ? undefined : LifeBuoy,
                iconColor: "text-orange-500",
                count: feature.count
              })
            })

            // Add accessibility (indigo icons) with actual counts
            const accessibility = parseBadgeArray(attributes.confirmed_accessibility)
            accessibility.forEach((access) => {
              const emoji = getFilterEmoji(access.label)
              allBadges.push({ 
                label: access.label,
                emoji: emoji || undefined,
                iconColor: "text-indigo-500",
                count: access.count
              })
            })

            if (allBadges.length === 0) {
              return null
            }

            // Calculate max count for normalization
            const maxCount = Math.max(...allBadges.map(b => b.count), 1)
            
            // Function to get blue shade based on popularity (darker = more popular)
            const getBlueShade = (count: number, maxCount: number) => {
              const ratio = count / maxCount
              if (ratio >= 0.8) return "bg-blue-700" // Darkest - most popular
              if (ratio >= 0.6) return "bg-blue-600"
              if (ratio >= 0.4) return "bg-blue-500" // Default
              if (ratio >= 0.2) return "bg-blue-400"
              return "bg-blue-300" // Lightest - least popular
            }

            return (
              <div className="flex flex-wrap gap-2">
                {allBadges.map((badge, index) => {
                  const Icon = badge.icon
                  const blueShade = getBlueShade(badge.count, maxCount)
                  return (
                    <Button
                      key={`${badge.label}-${index}`}
                      type="button"
                      variant="default"
                      className={`justify-start px-3 py-2 text-xs h-auto ${blueShade} text-white hover:opacity-90 border-transparent`}
                      disabled
                    >
                      {badge.emoji && <span className="mr-1 text-sm">{badge.emoji}</span>}
                      {Icon && <Icon className={`w-3 h-3 mr-1 ${badge.iconColor}`} />}
                      <span className="truncate">{formatLabel(badge.label)}</span>
                    </Button>
                  )
                })}
              </div>
            )
          })()}

          {/* Data Freshness Indicator */}
          <div className="pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {attributes.recent_review_count > 0 ? (
                <>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    {attributes.recent_review_count}
                  </span>{" "}
                  review{attributes.recent_review_count !== 1 ? "s" : ""} in the last 12 months
                </>
              ) : (
                "No recent reviews. Data may be outdated."
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {!user && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <Link href="/auth/register">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg">
                <Lock className="w-4 h-4 mr-2" />
                Join Community to See Insights
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
